<template>
  <div class="warnings-center">
    <toolbar
      backRoute="/dashboard"
      title="Warnings / alerts"
      actionButtonLabel="Refresh"
      actionButtonIcon="autorenew"
      :actionButtonCallback="loadFilteredData" />

    <d-row>
      <d-card class="w-100 pl-3 pr-3 pt-3 pb-3 mt-5">
        <d-row>
          <d-col cols="12" lg="4">
            <d-input
              v-model="search"
              placeholder="Search warnings"
              class="w-100" />
          </d-col>

          <d-col cols="12" lg="5">
            <date-range-picker
              ref="picker"
              :locale-data="{
                'firstDay': 1,
                'format': 'dMMMyy'
              }"
              class="w-100 text-center"
              :singleDatePicker="false"
              :timePicker="true"
              :showWeekNumbers="false"
              :showDropdowns="true"
              :autoApply="true"
              v-model="dateRange"
              :ranges="false"
              :linkedCalendars="true">
              <template v-slot:input="picker" style="min-width: 350px;">
                <template v-if="picker.startDate === null || (picker.startDate.toString() !== picker.endDate.toString())">
                  {{ picker.startDate | dateStart }} - {{ picker.endDate | dateEnd }}
                </template>
                <template v-if="picker.startDate !== null && picker.startDate.toString() === picker.endDate.toString()">
                  {{ picker.startDate | dateStart }} - not specified
                </template>
              </template>
            </date-range-picker>
          </d-col>

          <d-col cols="12" lg="3">
            <d-button
              theme="primary"
              outline
              class="ml-auto"
              @click.prevent="debouncedLoadFilteredData()">
              Filter warnings
            </d-button>

            <d-button
              theme="success"
              outline
              class="ml-4"
              @click.prevent="addAdminWarning()">
              Add warning
            </d-button>
          </d-col>
        </d-row>
      </d-card>
    </d-row>

    <d-row class="mt-5 mb-5">
      <d-card-group class="w-100">
        <d-card
          v-for="(group, index) of warningGroups"
          :key="'warning-group-' + index">
          <d-card-body
            :title="group.title"
            :class="'warnings-center-column warnings-center-column-' + group.severity + ' px-0 pt-2 pb-2'">
            <a
              v-if="!archivedItemsVisibility(group.severity)"
              href="#"
              class="toggle-archive-button"
              @click.prevent="showArchivedItems(group.severity)">
              <i class="material-icons">archive</i>
            </a>

            <a
              v-if="archivedItemsVisibility(group.severity)"
              href="#"
              class="toggle-archive-button"
              @click.prevent="hideArchivedItems(group.severity)">
              <i class="material-icons">unarchive</i>
            </a>

            <table class="table table-sm table-striped">
              <thead>
                <tr>
                  <th style="width: 30px;"></th>
                  <th style="width: 130px;">Time</th>
                  <th style="width: calc(100% - 160px)">Description</th>
                </tr>
              </thead>
              <tbody>
                <template v-if="loaded">
                  <tr
                    v-for="(warning, index) of filteredWarnings(group.warnings, group.severity)"
                    :key="'warning-medium-item-' + index"
                    :class="{ 'is-new': isNewWarning(warning.created_at), 'is-archived': warning.is_archived }">
                    <td class="align-middle text-center warning-icon" :title="warning.reference_name">
                      <i class="material-icons">
                        {{ getIcon(warning.reference_type) }}
                      </i>
                    </td>
                    <td class="align-middle">
                      <small>
                        {{ warning.valid_from | filterValidTo }}
                        <br>
                        {{ warning.valid_to | filterValidTo }}
                      </small>
                    </td>
                    <td class="align-middle warning-desc" :title="warning.message">
                      <small class="text-intro">
                        {{ warning.message }}<br>
                        <strong :class="'text-' + (group.severity === 'high' ? 'danger' : (group.severity === 'medium' ? 'warning' : 'secondary'))">{{ warning.reference_name }}</strong>
                      </small>

                      <div :class="{ 'actions': true, 'remove-in-progress': removeInProgress }">
                        <a
                          href="#"
                          class="warning-action is-active"
                          @click.prevent="editWarning(warning.id)"
                          title="Edit">
                          <i class="material-icons">edit</i>
                        </a>

                        <a
                          v-if="warning.can_be_archived && !warning.is_archived"
                          href="#"
                          :class="{ 'warning-action': true, 'is-active': !archiveInProgress }"
                          @click.prevent="archiveWarning(warning.id)"
                          title="Archive">
                          <i class="material-icons">archive</i>
                        </a>

                        <a
                          v-if="warning.can_be_archived && warning.is_archived"
                          href="#"
                          :class="{ 'warning-action': true, 'is-active': !archiveInProgress }"
                          @click.prevent="unarchiveWarning(warning.id)"
                          title="Unarchive">
                          <i class="material-icons">unarchive</i>
                        </a>

                        <a
                          href="#"
                          :class="{ 'warning-action': true, 'is-active': !removeInProgress }"
                          @click.prevent="removeWarning(warning.id)"
                          title="Delete">
                          <i class="material-icons">delete</i>
                        </a>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </d-card-body>
        </d-card>
      </d-card-group>
    </d-row>
  </div>
</template>

<script>
import DateRangePicker from 'vue2-daterange-picker';
import DateUtils from './../utils/DateUtils.js';
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import ListUtils from './../utils/ListUtils.js';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';

export default {
  name: 'warnings',
  components: {
    'date-range-picker': DateRangePicker
  },
  computed: {
    warningGroups () {
      return [
        {
          title: 'High',
          warnings: this.highSeverityWarnings,
          severity: 'high'
        },
        {
          title: 'Medium',
          warnings: this.mediumSeverityWarnings,
          severity: 'medium'
        },
        {
          title: 'Low',
          warnings: this.lowSeverityWarnings,
          severity: 'low'
        }
      ];
    },
    allDataLoaded () {
      return this.loaded;
    },
    highSeverityWarnings () {
      if (!this.warnings) {
        return [];
      }

      return this.warnings.filter(warning => warning.severity_level === 'HIGH' || warning.severity_level === '3').reverse();
    },
    mediumSeverityWarnings () {
      if (!this.warnings) {
        return [];
      }

      return this.warnings.filter(warning => warning.severity_level === 'MEDIUM' || warning.severity_level === '2').reverse();
    },
    lowSeverityWarnings () {
      if (!this.warnings) {
        return [];
      }

      return this.warnings.filter(warning => warning.severity_level === 'LOW' || warning.severity_level === '1').reverse();
    }
  },
  filters: {
    filterValidTo (validToDate) {
      if (validToDate === null) {
        return 'not specified';
      }

      return validToDate;
    },
    dateStart (input) {
      if (input === null) {
        return ' from ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      let time = [
        input.getHours(),
        input.getMinutes()
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date + ' ' + time;
    },
    dateEnd (input) {
      if (input === null) {
        return ' to ';
      }

      let date = [
        input.getDate(),
        DateUtils.getMonthShortName(input.getMonth() + 1),
        input.getFullYear().toString().substr(2, 2)
      ].map(n => n < 10 ? '0' + n : n).join('');

      let time = [
        input.getHours(),
        input.getMinutes()
      ].map(n => n < 10 ? '0' + n : n).join('');

      return date + ' ' + time;
    }
  },
  data () {
    return {
      loaded: false,
      warnings: null,
      loadError: false,
      dateRange: {
        startDate: null,
        endDate: null
      },
      archivedItemsDisplay: {
        high: false,
        medium: false,
        low: false
      },
      search: '',
      removeInProgress: false,
      archiveInProgress: false
    };
  },
  mounted () {
    this.loadFilteredData();
    this.$bus.$on('warnings-list-reload', this.debouncedLoadFilteredData);
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      this.loaded = false;
      let dateFrom = this.prepareDateString(this.dateRange.startDate);
      let dateTo = this.prepareDateString(this.dateRange.endDate);

      if (dateFrom === dateTo) {
        dateTo = null;
      }

      ListUtils.loadItemsData(this, '/warnings/items', 'warnings', {
        where: {
          pagination: false,
          search: this.search,
          date_from: dateFrom,
          date_to: dateTo
        }
      });
    },
    getIcon (type) {
      switch (type) {
        case 'AIRCRAFT': return 'local_airport';
        case 'AIRPORT': return 'flight_takeoff';
        case 'COUNTRY': return 'language';
        case 'CREW': return 'face';
        case 'CUSTOMER': return 'work';
        case 'HOTEL': return 'local_hotel';
        case 'PASSENGER': return 'airline_seat_recline_normal';
        default: return 'warning';
      }
    },
    prepareDateString (input) {
      if (input === null) {
        return null;
      }

      return [input.getFullYear(), input.getMonth() + 1, input.getDate()].map(n => n < 10 ? '0' + n : n).join('-') + ' ' + [input.getHours(), input.getMinutes()].map(n => n < 10 ? '0' + n : n).join(':');
    },
    addAdminWarning () {
      this.$bus.$emit('show-warning-popup', 'add', 'ADMIN', 0);
    },
    isNewWarning (dateString) {
      let warningDate = new Date(dateString).getTime();
      let currentDate = new Date().getTime();

      return currentDate - warningDate < 24 * 60 * 60 * 1000;
    },
    removeWarning (id) {
      if (this.removeInProgress) {
        return;
      }

      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/warnings/delete/',
        id: parseInt(id, 10),
        successTitle: 'Warning removed',
        successText: 'The selected warning has been removed',
        confirmTitle: 'Do you really want to remove this warning?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$bus.$emit('warnings-list-reload');
          this.$bus.$emit('calendar-warnings-update');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        },
        errorAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    editWarning (id) {
      this.$bus.$emit('show-warning-popup', 'edit', false, false, id);
    },
    archiveWarning (id) {
      if (this.archiveInProgress) {
        return;
      }

      this.archiveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/warnings/archive/' + parseInt(id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during adding a warning to archive. Please try again.',
        successAction: () => {
          this.archiveInProgress = false;
          this.$bus.$emit('calendar-warnings-update');
          this.$bus.$emit('warnings-list-reload');
        }
      });
    },
    unarchiveWarning (id) {
      if (this.archiveInProgress) {
        return;
      }

      this.archiveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/warnings/unarchive/' + parseInt(id, 10),
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during removing a warning from archive. Please try again.',
        successAction: () => {
          this.archiveInProgress = false;
          this.$bus.$emit('calendar-warnings-update');
          this.$bus.$emit('warnings-list-reload');
        }
      });
    },
    archivedItemsVisibility (severity) {
      return this.archivedItemsDisplay[severity];
    },
    showArchivedItems (severity) {
      this.archivedItemsDisplay[severity] = true;
    },
    hideArchivedItems (severity) {
      this.archivedItemsDisplay[severity] = false;
    },
    filteredWarnings (warnings, severity) {
      if (this.archivedItemsDisplay[severity]) {
        return warnings;
      }

      return warnings.filter(warning => !warning.is_archived);
    }
  },
  beforeDestroy () {
    this.$bus.$off('warnings-list-reload', this.debouncedLoadFilteredData);
  }
};
</script>

<style lang="scss" scoped>
.warnings-center-column {
  /deep/ .card-title {
    color: var(--white);
    font-size: 18px;
    margin: -10px 0 0 0;
    padding: 5px 10px;
  }
}

.warnings-center-column-high {
  /deep/ .card-title {
    background: var(--danger);
    border-radius: 10px 0 0 0;
  }
}

.warnings-center-column-medium {
  /deep/ .card-title {
    background: var(--warning);
    margin-left: -1px;
    margin-right: -1px;
  }
}

.warnings-center-column-low {
  /deep/ .card-title {
    background: var(--secondary);
    border-radius: 0 10px 0 0;
  }
}

.warnings-center {
  .table {
    table-layout: fixed;
  }

  .text-intro {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .vue-daterange-picker {
    /deep/ .form-control {
      padding: 6px 10px;
    }
  }

  .warning-icon {
    cursor: help;
  }

  .warning-desc {
    position: relative;

    .actions {
      font-size: 24px;
      opacity: 0;
      position: absolute;
      pointer-events: none;
      right: 0;
      top: 50%;
      transition: all .25s ease-out;
      transform: translateY(-50%);

      .warning-action {
        color: var(--secondary);
        margin-left: 5px;
      }
    }

    &:hover {
      .actions {
        opacity: 1;
        pointer-events: auto;
        right: 12px;
      }
    }
  }

  .is-new {
    background: rgba(249, 215, 28, .25);
  }

  .is-archived {
    opacity: .5;
  }

  .toggle-archive-button {
    color: var(--white);
    font-size: 28px;
    position: absolute;
    right: 10px;
    top: 0;
  }
}
</style>
